import React, { useCallback } from 'react';

import type { RelativeUnit } from '../../../../../apis/graphql/types';
import { DateOperator, RelativeDirection } from '../../../../../apis/graphql/types';
import type { DropdownOption } from '../../../../../common/Dropdown';
import Dropdown from '../../../../../common/Dropdown';
import bem from '../../../../../utils/bem';
import {
  clampRelativeValue,
  DEFAULT_RELATIVE_VALUE,
  MAX_RELATIVE_VALUE,
  MIN_RELATIVE_VALUE,
} from './relativeDateBounds';
import {
  getUnitOptionLabel,
  RELATIVE_DIRECTION_LABELS,
  RELATIVE_DIRECTION_ORDER,
  RELATIVE_UNIT_ORDER,
} from './relativeDateLabels';
import { todayLocalIso } from './relativeDateRange';
import type { RelativeDateFilter } from './types';

// Re-exported for callers that imported these from here before they moved to relativeDateBounds.
export { DEFAULT_RELATIVE_VALUE, MAX_RELATIVE_VALUE, MIN_RELATIVE_VALUE };

/**
 * KATANA-1752: the conditional-boundary caller (DateControl) reuses this component for a value
 * that starts unset (no direction, no unit) rather than the always-complete `RelativeDateFilter`
 * the `IS_RELATIVE_TO_TODAY` branch passes. `operator` is widened too — this component never
 * reads it, and `emit()` always hardcodes `IS_RELATIVE_TO_TODAY` on the way out regardless of
 * what's passed in, so requiring it here would only be a placeholder with no runtime meaning.
 * `value` (amount) and `referenceDate` stay required — the conditional caller always supplies a
 * concrete `null`/today respectively, even for an otherwise-unset triple.
 */
export type PartialRelativeValue = Omit<RelativeDateFilter, 'operator' | 'direction' | 'unit'> & {
  operator?: RelativeDateFilter['operator'];
  direction?: RelativeDirection;
  unit?: RelativeUnit;
};

export type RelativeDateControlProps = {
  value: PartialRelativeValue;
  onChange: (value: RelativeDateFilter) => void;
  /** Test-only escape hatch so specs need not mock the clock. `DateControl` owns this normally. */
  initialReferenceDate?: string;
  /** Defaults to 'relative-date-control' so the existing relative e2e helper stays green. The
   * conditional-boundary caller overrides this to 'conditional-boundary-control' to disambiguate. */
  dataTestId?: string;
};

const [block] = bem('date-control');

const DIRECTION_OPTIONS: DropdownOption[] = RELATIVE_DIRECTION_ORDER.map((direction) => ({
  label: RELATIVE_DIRECTION_LABELS[direction],
  value: direction,
}));

const UNIT_OPTIONS: DropdownOption[] = RELATIVE_UNIT_ORDER.map((unit) => ({
  label: getUnitOptionLabel(unit),
  value: unit,
}));

// An empty input is "cleared", not "invalid" — snapping it to MIN would fight the next keystroke.
const clampValue = (raw: string): number | null => (raw.trim() === '' ? null : clampRelativeValue(Number(raw)));

const RelativeDateControl = ({
  value,
  onChange,
  initialReferenceDate,
  dataTestId = 'relative-date-control',
}: RelativeDateControlProps): JSX.Element => {
  const referenceDate = value.referenceDate || initialReferenceDate || todayLocalIso();
  const isThis = value.direction === RelativeDirection.This;

  const emit = useCallback(
    (patch: Partial<Omit<RelativeDateFilter, 'operator'>>): void => {
      onChange({
        operator: DateOperator.IsRelativeToToday,
        direction: value.direction,
        value: value.value,
        unit: value.unit,
        referenceDate,
        ...patch,
        // KATANA-1752: `value.direction`/`value.unit` are `| undefined` now that `value` is
        // widened to `PartialRelativeValue`. `onChange`'s declared type stays `RelativeDateFilter`
        // unchanged — the IS_RELATIVE_TO_TODAY caller always supplies a complete value, so this
        // object is always genuinely complete for that caller. The conditional-boundary caller
        // (DateControl) deliberately passes a value that can still be missing direction/unit and
        // reads this callback's result the same way: as a possibly-partial patch, not a trusted
        // complete filter, deciding completeness itself before committing anything real. This
        // cast reflects that split responsibility rather than fabricating a fake default.
      } as RelativeDateFilter);
    },
    [onChange, referenceDate, value.direction, value.unit, value.value]
  );

  const handleDirectionChange = useCallback(
    (option: DropdownOption | null): void => {
      if (!option) return;
      const direction = option.value as RelativeDirection;
      if (direction === value.direction) return;

      // Cleared in the same emit as the direction change — a follow-up effect would commit
      // `{direction: THIS, value: 4}` for a render, which the backend validator rejects.
      if (direction === RelativeDirection.This) {
        emit({ direction, value: null });
        return;
      }

      // Leaving THIS: the value was genuinely cleared, and `{direction: NEXT, value: null}` is invalid.
      const restored = value.value ?? DEFAULT_RELATIVE_VALUE;
      emit({ direction, value: restored });
    },
    [emit, value.direction, value.value]
  );

  const handleValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (isThis) return;
      emit({ value: clampValue(event.target.value) });
    },
    [emit, isThis]
  );

  const handleUnitChange = useCallback(
    (option: DropdownOption | null): void => {
      if (!option) return;
      emit({ unit: option.value as RelativeUnit });
    },
    [emit]
  );

  const selectedDirection = DIRECTION_OPTIONS.find((o) => o.value === value.direction) ?? null;
  const selectedUnit = UNIT_OPTIONS.find((o) => o.value === value.unit) ?? null;

  return (
    <div className={`${block()}__relative`} data-testid={dataTestId}>
      <Dropdown
        options={DIRECTION_OPTIONS}
        value={selectedDirection}
        onChange={handleDirectionChange}
        emptyDisplayText="Select direction"
        dataTestId="relative-date-direction-dropdown"
        aria-label="Relative date direction"
      />
      <input
        className={`${block()}__relative-value`}
        type="number"
        min={MIN_RELATIVE_VALUE}
        max={MAX_RELATIVE_VALUE}
        step={1}
        value={value.value ?? ''}
        disabled={isThis}
        onChange={handleValueChange}
        data-testid="relative-date-value-input"
        aria-label="Relative date value"
      />
      <Dropdown
        options={UNIT_OPTIONS}
        value={selectedUnit}
        onChange={handleUnitChange}
        emptyDisplayText="Select unit"
        dataTestId="relative-date-unit-dropdown"
        aria-label="Relative date unit"
      />
    </div>
  );
};

export default RelativeDateControl;
